// Leitor de decisões: o PDF é lido no próprio navegador; só o texto segue para a IA, e nada é salvo.
import { supabase } from '@/integrations/supabase/client';

export interface DecisionDeadline {
  descricao: string;
  dias: number | null;
  contagem: 'uteis' | 'corridos' | 'nao_informado';
  trecho: string;
  trechoConfere: boolean;
}

export interface DecisionHearing {
  data: string | null;
  descricao: string;
  trecho: string;
  trechoConfere: boolean;
}

export interface DecisionReading {
  numeroProcesso: string | null;
  tipoAto: string;
  resultado: string;
  resumo: string;
  determinacoes: { quem: string; o_que: string }[];
  prazos: DecisionDeadline[];
  audiencias: DecisionHearing[];
  pontosDeAtencao: string[];
}

const MAX_PDF_BYTES = 25 * 1024 * 1024;

export async function extractPdfText(file: File): Promise<{ text: string; pages: number }> {
  if (file.size > MAX_PDF_BYTES) throw new Error('O PDF tem mais de 25 MB.');
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.mjs';
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  const parts: string[] = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const content = await page.getTextContent();
    parts.push(content.items.map((item: any) => `${item.str || ''}${item.hasEOL ? '\n' : ' '}`).join(''));
  }
  return { text: parts.join('\n\n').trim(), pages: pdf.numPages };
}

const squash = (text: string) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '')
    .toLowerCase();

/** O trecho citado pela IA existe mesmo no documento? */
export const quoteFound = (quote: string, source: string) => {
  const needle = squash(quote);
  return needle.length >= 8 && squash(source).includes(needle);
};

const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/** Converte a resposta da IA e marca o que não foi possível conferir no texto original */
export function parseReading(raw: string, source: string): DecisionReading {
  let data: any;
  try {
    data = JSON.parse(raw.replace(/^```(?:json)?\s*|\s*```$/g, ''));
  } catch {
    throw new Error('A IA devolveu uma resposta fora do formato. Tente de novo.');
  }
  return {
    numeroProcesso: data.numero_processo || null,
    tipoAto: String(data.tipo_ato || 'Outro'),
    resultado: String(data.resultado || ''),
    resumo: String(data.resumo || ''),
    determinacoes: list<any>(data.determinacoes).map((d) => ({ quem: String(d?.quem || ''), o_que: String(d?.o_que || '') })),
    prazos: list<any>(data.prazos).map((p) => {
      const dias = Number(p?.dias);
      return {
        descricao: String(p?.descricao || ''),
        dias: Number.isInteger(dias) && dias > 0 ? dias : null,
        contagem: p?.contagem === 'uteis' || p?.contagem === 'corridos' ? p.contagem : 'nao_informado',
        trecho: String(p?.trecho || ''),
        trechoConfere: quoteFound(p?.trecho, source),
      };
    }),
    audiencias: list<any>(data.audiencias).map((a) => ({
      data: a?.data || null,
      descricao: String(a?.descricao || ''),
      trecho: String(a?.trecho || ''),
      trechoConfere: quoteFound(a?.trecho, source),
    })),
    pontosDeAtencao: list<string>(data.pontos_de_atencao).map(String),
  };
}

export async function readDecision(source: string): Promise<DecisionReading> {
  const { data, error } = await supabase.functions.invoke('ai-assistant', {
    body: { prompt: 'Leia a decisão abaixo.', context: source, task_type: 'decision_reader' },
  });
  if (error) throw new Error('Não foi possível falar com a IA. Tente de novo.');
  if (!data?.success) throw new Error(data?.error || 'A IA não respondeu.');
  return parseReading(data.response, source);
}
